import React, { useState } from 'react';
import {
  MapPin,
  Clock,
  Star,
  Phone,
  Globe,
  Calendar,
  History,
  ChevronRight,
  ChevronDown,
  Award,
  Coffee,
  Cigarette,
  Users,
  ArrowLeft,
  ExternalLink,
  Sparkles,
  DollarSign,
} from 'lucide-react';
import {
  CityGuide,
  CityLounge,
  LocalBrand,
  CityEvent,
  HistoricalNote,
  getLiveCities,
  getCityById,
} from '../data/cities';

interface CityGuideViewProps {
  onBack: () => void;
}

// City Selector Component
const CitySelector: React.FC<{
  cities: CityGuide[];
  selectedCity: CityGuide;
  onSelectCity: (city: CityGuide) => void;
}> = ({ cities, selectedCity, onSelectCity }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-stone-800 border border-amber-900/30 rounded-lg text-amber-100 hover:bg-stone-700 transition-colors"
      >
        <MapPin className="w-4 h-4 text-amber-500" />
        <span className="font-medium">{selectedCity.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-stone-800 border border-amber-900/30 rounded-lg shadow-xl z-50 overflow-hidden">
          {cities.map((city) => (
            <button
              key={city.id}
              onClick={() => {
                onSelectCity(city);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-stone-700 transition-colors ${
                city.id === selectedCity.id ? 'bg-amber-900/20' : ''
              }`}
            >
              <MapPin className={`w-4 h-4 ${city.id === selectedCity.id ? 'text-amber-500' : 'text-stone-500'}`} />
              <div>
                <p className={`font-medium ${city.id === selectedCity.id ? 'text-amber-100' : 'text-stone-300'}`}>
                  {city.name}
                </p>
                <p className="text-xs text-stone-500">{city.tagline}</p>
              </div>
              {city.id === selectedCity.id && (
                <Star className="w-4 h-4 text-amber-500 ml-auto" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Price Range Display
const PriceRange: React.FC<{ range: string }> = ({ range }) => (
  <span className="flex items-center gap-0.5 text-amber-500/70">
    {[1, 2, 3].map((i) => (
      <DollarSign
        key={i}
        className={`w-3 h-3 ${i <= range.length ? 'opacity-100' : 'opacity-30'}`}
      />
    ))}
  </span>
);

// Atmosphere Badge
const AtmosphereBadge: React.FC<{ atmosphere: CityLounge['atmosphere'] }> = ({ atmosphere }) => {
  const colors = {
    casual: 'bg-green-500/20 text-green-400',
    upscale: 'bg-purple-500/20 text-purple-400',
    historic: 'bg-amber-500/20 text-amber-400',
    modern: 'bg-blue-500/20 text-blue-400',
    neighborhood: 'bg-stone-500/20 text-stone-400',
  };

  return (
    <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${colors[atmosphere]}`}>
      {atmosphere}
    </span>
  );
};

// Lounge Card Component
const LoungeCard: React.FC<{ lounge: CityLounge }> = ({ lounge }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-stone-800/50 border border-amber-900/20 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 text-left"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-amber-100">{lounge.name}</h3>
              {lounge.featured && (
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Featured
                </span>
              )}
            </div>
            <p className="text-sm text-stone-400 mb-2 line-clamp-2">{lounge.description}</p>
            <div className="flex items-center gap-3 text-xs text-stone-500">
              <AtmosphereBadge atmosphere={lounge.atmosphere} />
              <PriceRange range={lounge.priceRange} />
              {lounge.neighborhood && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {lounge.neighborhood}
                </span>
              )}
            </div>
          </div>
          <ChevronRight className={`w-5 h-5 text-stone-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-stone-700/50 pt-3">
          <p className="text-sm text-stone-300">{lounge.description}</p>

          <div className="flex flex-wrap gap-2">
            {lounge.amenities.map((amenity, idx) => (
              <span key={idx} className="px-2 py-1 bg-stone-700/50 text-stone-400 text-xs rounded">
                {amenity}
              </span>
            ))}
          </div>

          {lounge.specialties && lounge.specialties.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-amber-500 mb-1">Specialties</p>
              <div className="flex flex-wrap gap-1">
                {lounge.specialties.map((specialty, idx) => (
                  <span key={idx} className="px-2 py-0.5 bg-amber-900/20 text-amber-400 text-xs rounded">
                    {specialty}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-stone-400">
              <MapPin className="w-4 h-4 text-amber-500/70" />
              <span>{lounge.address}</span>
            </div>
            {lounge.hours && (
              <div className="flex items-center gap-2 text-stone-400">
                <Clock className="w-4 h-4 text-amber-500/70" />
                <span>{lounge.hours}</span>
              </div>
            )}
            {lounge.phone && (
              <div className="flex items-center gap-2 text-stone-400">
                <Phone className="w-4 h-4 text-amber-500/70" />
                <a href={`tel:${lounge.phone}`} className="hover:text-amber-400 transition-colors">
                  {lounge.phone}
                </a>
              </div>
            )}
            {lounge.website && (
              <div className="flex items-center gap-2 text-stone-400">
                <Globe className="w-4 h-4 text-amber-500/70" />
                <a
                  href={lounge.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-amber-400 transition-colors flex items-center gap-1"
                >
                  Visit Website
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Local Brand Card Component
const BrandCard: React.FC<{ brand: LocalBrand }> = ({ brand }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-stone-800/50 border border-amber-900/20 rounded-xl p-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-900/20 rounded-lg flex-shrink-0">
            <Award className="w-5 h-5 text-amber-500" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-amber-100">{brand.name}</h3>
              {brand.featured && (
                <Sparkles className="w-4 h-4 text-amber-500" />
              )}
            </div>
            {brand.founded && (
              <p className="text-xs text-stone-500 mb-2">Est. {brand.founded}</p>
            )}
            <p className="text-sm text-stone-300 line-clamp-2">{brand.story}</p>
          </div>
          <ChevronRight className={`w-5 h-5 text-stone-500 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-stone-700/50 space-y-3">
          <p className="text-sm text-stone-300">{brand.story}</p>

          <div>
            <p className="text-xs text-amber-500 mb-1">Notable Cigars</p>
            <div className="flex flex-wrap gap-1">
              {brand.notableCigars.map((cigar, idx) => (
                <span key={idx} className="px-2 py-0.5 bg-stone-700/50 text-stone-400 text-xs rounded">
                  {cigar}
                </span>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs text-amber-500 mb-1">Where to Find</p>
            <ul className="text-sm text-stone-400 space-y-1">
              {brand.whereToFind.map((location, idx) => (
                <li key={idx} className="flex items-center gap-2">
                  <MapPin className="w-3 h-3 text-stone-500" />
                  {location}
                </li>
              ))}
            </ul>
          </div>

          {brand.website && (
            <a
              href={brand.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300"
            >
              <Globe className="w-4 h-4" />
              Visit Website
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

// Event Card Component
const EventCard: React.FC<{ event: CityEvent }> = ({ event }) => {
  const typeIcons: Record<CityEvent['type'], React.ElementType> = {
    festival: Users,
    tasting: Coffee,
    release: Cigarette,
    meetup: Users,
    recurring: Calendar,
  };
  const Icon = typeIcons[event.type];

  const typeColors: Record<CityEvent['type'], string> = {
    festival: 'bg-amber-500/20 text-amber-400',
    tasting: 'bg-purple-500/20 text-purple-400',
    release: 'bg-green-500/20 text-green-400',
    meetup: 'bg-blue-500/20 text-blue-400',
    recurring: 'bg-stone-500/20 text-stone-400',
  };

  return (
    <div className="bg-stone-800/50 border border-amber-900/20 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-amber-900/20 rounded-lg">
          <Icon className="w-5 h-5 text-amber-500" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-amber-100">{event.name}</h3>
            <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${typeColors[event.type]}`}>
              {event.type}
            </span>
          </div>
          <p className="text-sm text-stone-300 mb-2">{event.description}</p>

          <div className="flex items-center gap-4 text-xs text-stone-500">
            {event.date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {event.date}
              </span>
            )}
            {event.venue && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {event.venue}
              </span>
            )}
          </div>

          {event.link && (
            <a
              href={event.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300 mt-2"
            >
              Learn More
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// History Timeline Component
const HistoryTimeline: React.FC<{ history: HistoricalNote[] }> = ({ history }) => {
  const [showAll, setShowAll] = useState(false);
  const displayHistory = showAll ? history : history.slice(0, 3);

  return (
    <div className="space-y-4">
      {displayHistory.map((note, idx) => (
        <div key={note.id} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className="w-3 h-3 bg-amber-500 rounded-full" />
            {idx < displayHistory.length - 1 && (
              <div className="w-0.5 h-full bg-amber-900/30 mt-1" />
            )}
          </div>
          <div className="pb-4">
            {note.year && (
              <p className="text-amber-500 font-medium text-sm">{note.year}</p>
            )}
            <h4 className="text-amber-100 font-medium">{note.title}</h4>
            <p className="text-sm text-stone-400 mt-1">{note.content}</p>
          </div>
        </div>
      ))}

      {history.length > 3 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="text-amber-500 text-sm hover:text-amber-400 transition-colors flex items-center gap-1"
        >
          {showAll ? 'Show Less' : `Show ${history.length - 3} More`}
          <ChevronDown className={`w-4 h-4 transition-transform ${showAll ? 'rotate-180' : ''}`} />
        </button>
      )}
    </div>
  );
};

// Section Header Component
const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  count?: number;
}> = ({ icon, title, count }) => (
  <div className="flex items-center gap-2 mb-4">
    <div className="p-1.5 bg-amber-900/20 rounded-lg">
      {icon}
    </div>
    <h2 className="text-lg font-semibold text-amber-100">{title}</h2>
    {count !== undefined && (
      <span className="px-2 py-0.5 bg-stone-700/50 text-stone-400 text-xs rounded-full">
        {count}
      </span>
    )}
  </div>
);

// Main CityGuideView Component
export const CityGuideView: React.FC<CityGuideViewProps> = ({ onBack }) => {
  const cities = getLiveCities();
  const [selectedCity, setSelectedCity] = useState<CityGuide>(
    getCityById('ybor-city') || cities[0]
  );
  const [activeTab, setActiveTab] = useState<'lounges' | 'brands' | 'events' | 'history'>('lounges');

  const tabs = [
    { id: 'lounges' as const, label: 'Lounges', count: selectedCity.lounges.length },
    { id: 'brands' as const, label: 'Brands', count: selectedCity.localBrands.length },
    { id: 'events' as const, label: 'Events', count: selectedCity.events.length },
    { id: 'history' as const, label: 'History', count: selectedCity.history.length },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-900 via-stone-900 to-stone-950">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-stone-900/95 backdrop-blur-sm border-b border-amber-900/20">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-stone-400 hover:text-amber-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <CitySelector
              cities={cities}
              selectedCity={selectedCity}
              onSelectCity={setSelectedCity}
            />
          </div>

          {/* City Hero */}
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-amber-100">{selectedCity.name}</h1>
            <p className="text-stone-400">{selectedCity.tagline}</p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-amber-500 text-stone-900'
                    : 'bg-stone-800 text-stone-400 hover:text-stone-200'
                }`}
              >
                {tab.label}
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id
                    ? 'bg-stone-900/20 text-stone-900'
                    : 'bg-stone-700 text-stone-500'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        {/* Lounges Tab */}
        {activeTab === 'lounges' && (
          <div className="space-y-4">
            <SectionHeader
              icon={<Coffee className="w-4 h-4 text-amber-500" />}
              title="Cigar Lounges"
              count={selectedCity.lounges.length}
            />
            {selectedCity.lounges.map((lounge) => (
              <LoungeCard key={lounge.id} lounge={lounge} />
            ))}
          </div>
        )}

        {/* Brands Tab */}
        {activeTab === 'brands' && (
          <div className="space-y-4">
            <SectionHeader
              icon={<Award className="w-4 h-4 text-amber-500" />}
              title="Local Brands"
              count={selectedCity.localBrands.length}
            />
            {selectedCity.localBrands.map((brand) => (
              <BrandCard key={brand.id} brand={brand} />
            ))}
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div className="space-y-6">
            {/* Recurring Events - Always Available */}
            {selectedCity.events.filter(e => e.type === 'recurring').length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1.5 bg-green-500/20 rounded-lg">
                    <Calendar className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-amber-100">Regular Events</h2>
                    <p className="text-xs text-stone-500">Happening year-round</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {selectedCity.events
                    .filter(e => e.type === 'recurring')
                    .map((event) => (
                      <EventCard key={event.id} event={event} />
                    ))}
                </div>
              </div>
            )}

            {/* Special Events - Festivals, Tastings, etc */}
            {selectedCity.events.filter(e => e.type !== 'recurring').length > 0 && (
              <div>
                <SectionHeader
                  icon={<Calendar className="w-4 h-4 text-amber-500" />}
                  title="Special Events"
                  count={selectedCity.events.filter(e => e.type !== 'recurring').length}
                />
                <div className="space-y-3">
                  {selectedCity.events
                    .filter(e => e.type !== 'recurring')
                    .map((event) => (
                      <EventCard key={event.id} event={event} />
                    ))}
                </div>
              </div>
            )}

            {/* No events message */}
            {selectedCity.events.length === 0 && (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-stone-600 mx-auto mb-3" />
                <p className="text-stone-400">No events listed yet</p>
                <p className="text-sm text-stone-500 mt-1">Check back soon for updates</p>
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            <SectionHeader
              icon={<History className="w-4 h-4 text-amber-500" />}
              title="Cigar History"
            />
            <HistoryTimeline history={selectedCity.history} />

            {/* Cultural Tips */}
            {selectedCity.culturalTips.length > 0 && (
              <div className="mt-8">
                <SectionHeader
                  icon={<Sparkles className="w-4 h-4 text-amber-500" />}
                  title="Local Tips"
                />
                <div className="bg-stone-800/50 border border-amber-900/20 rounded-xl p-4">
                  <ul className="space-y-3">
                    {selectedCity.culturalTips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-stone-300">
                        <span className="text-amber-500">•</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Best Time to Visit */}
            {selectedCity.bestTimeToVisit && (
              <div className="bg-amber-900/10 border border-amber-900/20 rounded-xl p-4">
                <p className="text-xs text-amber-500 mb-1">Best Time to Visit</p>
                <p className="text-sm text-stone-300">{selectedCity.bestTimeToVisit}</p>
              </div>
            )}

            {/* Getting Around */}
            {selectedCity.gettingAround && (
              <div className="bg-stone-800/50 border border-amber-900/20 rounded-xl p-4">
                <p className="text-xs text-amber-500 mb-1">Getting Around</p>
                <p className="text-sm text-stone-300">{selectedCity.gettingAround}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Coming Soon Banner */}
      <div className="px-4 pb-8">
        <div className="bg-gradient-to-r from-amber-900/20 to-stone-800/50 border border-amber-900/30 rounded-xl p-4 text-center">
          <p className="text-amber-400 font-medium mb-1">More Cities Coming Soon</p>
          <p className="text-sm text-stone-500">Miami • New Orleans • Las Vegas</p>
        </div>
      </div>
    </div>
  );
};

export default CityGuideView;
